import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Box,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import useColors from "../../../../api/hooks/useColors";
import SecondaryButton from "../../../../components/buttons/SecondaryButton";

export const KeyValueEditor = ({ data, onChange }) => {
  const colors = useColors();
  const [entries, setEntries] = useState(() => {
    try {
      const parsed = typeof data === "string" ? JSON.parse(data) : data;
      return Object.entries(parsed || {});
    } catch {
      return [];
    }
  });

  const handleAdd = () => {
    const newEntries = [...entries, ["", ""]];
    setEntries(newEntries);
    updateParent(newEntries);
  };

  const handleDelete = (index) => {
    const newEntries = entries.filter((_, i) => i !== index);
    setEntries(newEntries);
    updateParent(newEntries);
  };

  const handleKeyChange = (index, newKey) => {
    const newEntries = [...entries];
    newEntries[index][0] = newKey;
    setEntries(newEntries);
    updateParent(newEntries);
  };

  const handleValueChange = (index, newValue) => {
    const newEntries = [...entries];
    newEntries[index][1] = newValue;
    setEntries(newEntries);
    updateParent(newEntries);
  };

  const updateParent = (newEntries) => {
    const obj = Object.fromEntries(newEntries.filter(([k]) => k !== ""));
    onChange(JSON.stringify(obj));
  };

  return (
    <Box>
      <Box
        sx={{
          mb: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Configure key-value pairs
        </Typography>
        <SecondaryButton
          onClick={handleAdd}
          startIcon={<AddIcon />}
          size="small"
        >
          Add Entry
        </SecondaryButton>
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {entries.map(([key, value], index) => (
          <Box
            key={index}
            sx={{
              display: "flex",
              gap: 1,
              p: 2,
              bgcolor: colors.grey_50,
              borderRadius: 1,
            }}
          >
            <TextField
              label="Key"
              value={key}
              onChange={(e) => handleKeyChange(index, e.target.value)}
              size="small"
              sx={{ flex: 1 }}
            />
            <TextField
              label="Value"
              value={value}
              onChange={(e) => handleValueChange(index, e.target.value)}
              size="small"
              sx={{ flex: 2 }}
            />
            <IconButton
              onClick={() => handleDelete(index)}
              color="error"
              size="small"
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        ))}
        {entries.length === 0 && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: "center", py: 2 }}
          >
            No entries. Click "Add Entry" to add a new key-value pair.
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export const BooleanKeyValueEditor = ({ data, onChange }) => {
  const colors = useColors();
  const [entries, setEntries] = useState(() => {
    try {
      const parsed = typeof data === "string" ? JSON.parse(data) : data;
      return Object.entries(parsed || {});
    } catch {
      return [];
    }
  });

  const handleAdd = () => {
    const newEntries = [...entries, ["", "false"]];
    setEntries(newEntries);
    updateParent(newEntries);
  };

  const handleDelete = (index) => {
    const newEntries = entries.filter((_, i) => i !== index);
    setEntries(newEntries);
    updateParent(newEntries);
  };

  const handleKeyChange = (index, newKey) => {
    const newEntries = [...entries];
    newEntries[index][0] = newKey;
    setEntries(newEntries);
    updateParent(newEntries);
  };

  const handleValueChange = (index, newValue) => {
    const newEntries = [...entries];
    newEntries[index][1] = newValue;
    setEntries(newEntries);
    updateParent(newEntries);
  };

  const updateParent = (newEntries) => {
    const obj = Object.fromEntries(newEntries.filter(([k]) => k !== ""));
    onChange(JSON.stringify(obj));
  };

  return (
    <Box>
      <Box
        sx={{
          mb: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Configure feature flags (true/false)
        </Typography>
        <SecondaryButton
          onClick={handleAdd}
          startIcon={<AddIcon />}
          size="small"
        >
          Add Flag
        </SecondaryButton>
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {entries.map(([key, value], index) => (
          <Box
            key={index}
            sx={{
              display: "flex",
              gap: 1,
              p: 2,
              bgcolor: colors.grey_50,
              borderRadius: 1,
              alignItems: "center",
            }}
          >
            <TextField
              label="Flag Name"
              value={key}
              onChange={(e) => handleKeyChange(index, e.target.value)}
              size="small"
              sx={{ flex: 2 }}
            />
            <FormControl size="small" sx={{ flex: 1 }}>
              <InputLabel>Enabled</InputLabel>
              <Select
                value={value}
                label="Enabled"
                onChange={(e) => handleValueChange(index, e.target.value)}
              >
                <MenuItem value="true">True</MenuItem>
                <MenuItem value="false">False</MenuItem>
              </Select>
            </FormControl>
            <IconButton
              onClick={() => handleDelete(index)}
              color="error"
              size="small"
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        ))}
        {entries.length === 0 && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: "center", py: 2 }}
          >
            No feature flags. Click "Add Flag" to add a new flag.
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export const ArrayEditor = ({ data, onChange }) => {
  const colors = useColors();
  const [items, setItems] = useState(() => {
    try {
      const parsed = typeof data === "string" ? JSON.parse(data) : data;
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  });

  const handleAdd = () => {
    const newItems = [...items, ""];
    setItems(newItems);
    updateParent(newItems);
  };

  const handleDelete = (index) => {
    const newItems = items.filter((_, i) => i !== index);
    setItems(newItems);
    updateParent(newItems);
  };

  const handleChange = (index, newValue) => {
    const newItems = [...items];
    newItems[index] = newValue;
    setItems(newItems);
    updateParent(newItems);
  };

  const updateParent = (newItems) => {
    onChange(JSON.stringify(newItems.filter((item) => item !== "")));
  };

  return (
    <Box>
      <Box
        sx={{
          mb: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Manage list items
        </Typography>
        <SecondaryButton
          onClick={handleAdd}
          startIcon={<AddIcon />}
          size="small"
        >
          Add Item
        </SecondaryButton>
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {items.map((item, index) => (
          <Box
            key={index}
            sx={{
              display: "flex",
              gap: 1,
              p: 2,
              bgcolor: colors.grey_50,
              borderRadius: 1,
            }}
          >
            <TextField
              label={`Item ${index + 1}`}
              value={item}
              onChange={(e) => handleChange(index, e.target.value)}
              size="small"
              fullWidth
            />
            <IconButton
              onClick={() => handleDelete(index)}
              color="error"
              size="small"
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        ))}
        {items.length === 0 && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: "center", py: 2 }}
          >
            No items. Click "Add Item" to add a new item.
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export const TimeZoneArrayEditor = ({ data, onChange }) => {
  const colors = useColors();
  const [tzData, setTzData] = useState(() => {
    try {
      const parsed = typeof data === "string" ? JSON.parse(data) : data;
      return parsed || {};
    } catch {
      return {};
    }
  });

  const timezones = Object.keys(tzData);

  const handleAddCustomer = (timezone) => {
    const newData = { ...tzData };
    newData[timezone] = [...(newData[timezone] || []), ""];
    setTzData(newData);
    updateParent(newData);
  };

  const handleDeleteCustomer = (timezone, index) => {
    const newData = { ...tzData };
    newData[timezone] = newData[timezone].filter((_, i) => i !== index);
    setTzData(newData);
    updateParent(newData);
  };

  const handleChangeCustomer = (timezone, index, value) => {
    const newData = { ...tzData };
    newData[timezone][index] = value;
    setTzData(newData);
    updateParent(newData);
  };

  const updateParent = (newData) => {
    const cleaned = Object.fromEntries(
      Object.entries(newData).map(([tz, customers]) => [
        tz,
        customers.filter((c) => c !== ""),
      ])
    );
    onChange(JSON.stringify(cleaned));
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Manage customers by timezone
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {timezones.map((timezone) => (
          <Box key={timezone}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
              }}
            >
              <Typography variant="subtitle2" fontWeight="bold">
                {timezone}
              </Typography>
              <SecondaryButton
                onClick={() => handleAddCustomer(timezone)}
                startIcon={<AddIcon />}
                size="small"
              >
                Add Customer
              </SecondaryButton>
            </Box>
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 1, pl: 2 }}
            >
              {tzData[timezone]?.map((customer, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    gap: 1,
                    p: 1.5,
                    bgcolor: colors.grey_50,
                    borderRadius: 1,
                  }}
                >
                  <TextField
                    label="Customer ID"
                    value={customer}
                    onChange={(e) =>
                      handleChangeCustomer(timezone, index, e.target.value)
                    }
                    size="small"
                    fullWidth
                    placeholder="customer-domain.com"
                  />
                  <IconButton
                    onClick={() => handleDeleteCustomer(timezone, index)}
                    color="error"
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ))}
              {(!tzData[timezone] || tzData[timezone].length === 0) && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ py: 1, pl: 1 }}
                >
                  No customers in this timezone
                </Typography>
              )}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};
